import { useT } from "../../../../i18n/I18nProvider";
import { type StaffUser } from "../../../../lib/auth";
import { PHONE_TAB_PERM } from "../perms";
import { SettingsSubsection, type MovedPath } from "../subsection";
import { INTEGRATION_TABS } from "./tabs";

/**
 * The nine phone panels answered under `/settings/integrations/<tab>` until the
 * phone stack became its own Settings tab. Those URLs were handed around - the
 * whole point of routing each panel was that a settings page somebody has to
 * talk about is a page worth being able to send - so they keep answering, one
 * redirect each.
 *
 * Derived from the permission registry rather than typed out again: it is the
 * React-free list of the same nine segments, so a phone tab renamed there
 * cannot leave a redirect here pointing at nothing.
 *
 * Card payments left this menu too, but its redirect is NOT here. This whole
 * subsection is only mounted for somebody who holds one of the Integrations
 * rights, and the operator most likely to have that bookmark is a sales one who
 * holds none of them - card payments was the entry that put `manage:crm.sales`
 * into `INTEGRATION_PERMS`, and it has gone. So that one redirect lives a level
 * up, in `SettingsSection`. The nine phone ones can stay here: every phone tab
 * still shares a right with something in this menu.
 */
const MOVED_TO_PHONE: MovedPath[] = Object.keys(PHONE_TAB_PERM).map((path) => ({
  from: path,
  to: `/settings/phone/${path}`,
}));


/**
 * Settings -> Integrations.
 *
 * The outside services the CRM talks to: the Business Register, the mailbox,
 * the sending channels, and the test-mode guard over all of them. Each is its
 * own routed tab, listed in a side menu.
 *
 * This used to be thirteen tabs, nine of which were the phone. Those live under
 * `/settings/phone` now - the menu here is the four that are left.
 */
export function IntegrationsSettings({ user }: { user: StaffUser }) {
  const t = useT();
  return (
    <SettingsSubsection
      base="/settings/integrations"
      tabs={INTEGRATION_TABS}
      user={user}
      navLabel={t("settings.integrations")}
      i18nPrefix="settings.integrationsNav"
      moved={MOVED_TO_PHONE}
    />
  );
}
